/* eslint-disable indent */
import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";
import { useCurrency } from "../../utils";
import { validarSumaCuotas } from "../../utils/cuotasUtil";
import { client } from "../../utils/supabase";

// <FacturacionHardwareModal> — declara el SPLIT de facturación del hardware CAPEX de un
// negocio de cartelería en 2 tramos POR PORCENTAJE sobre `monto_hardware`, en `moneda_hardware`.
// Fuente única: `negocios_carteleria.detalle_pagos_hardware` (regla 10.1). Facturación ya
// renderiza 1 fila por entry (VentasFacturas/useVFDataLoading, rama HW-CAPEX), así que 2 entries
// → 2 filas HW-CAPEX matcheables por `venta_ref_index` POSICIONAL (idx 0 / idx 1). Por eso el
// array NUNCA se reordena: tramo1 = idx 0, tramo2 = idx 1, siempre.

// Decimales por moneda: CLP entero; UF/USD/PEN con 2 (mismo criterio que CarteleriaForm).
const decimalesDe = (moneda) => ((moneda || "CLP").toUpperCase() === "CLP" ? 0 : 2);
const redondear = (v, dec) => {
  const f = 10 ** dec;
  return Math.round((Number(v) || 0) * f) / f;
};
const pad2 = (n) => String(n).padStart(2, "0");
// 'YYYY-MM-DD' → Date en horario LOCAL (evita el corrimiento UTC de `new Date('YYYY-MM-DD')`).
const parseFechaLocal = (str) => {
  if (!str) return null;
  const [y, m, d] = String(str).substring(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
// Date → 'YYYY-MM-DD' local (sin toISOString, que desplaza por zona).
const fechaISOLocal = (date) =>
  date ? `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}` : null;

const FacturacionHardwareModal = ({ negocio, onClose, onSaved }) => {
  const { formatAmount } = useCurrency();

  const moneda = negocio.moneda_hardware || "CLP";
  const dec = decimalesDe(moneda);
  const montoTotal = parseFloat(negocio.monto_hardware) || 0;

  // Cada tramo: { pct (string), fecha (Date|null), locked (bool), preserved (entry original|null) }.
  const [tramos, setTramos] = useState([]);
  const [cargando, setCargando] = useState(true);
  // Aviso de incompatibilidad (10.177): si el detalle existente NO encaja en el modelo de 2 tramos
  // por %, NO se sobrescribe — se bloquea Guardar y se muestra el motivo.
  const [incompatible, setIncompatible] = useState(null);
  const [guardando, setGuardando] = useState(false);

  // Precarga: parsea el detalle existente + detecta tramos ya facturados (candado read-only).
  useEffect(() => {
    let vivo = true;
    const cargar = async () => {
      setCargando(true);
      setIncompatible(null);

      const detalle =
        typeof negocio.detalle_pagos_hardware === "string"
          ? JSON.parse(negocio.detalle_pagos_hardware || "[]")
          : negocio.detalle_pagos_hardware || [];

      // Vínculos SII posicionales de este negocio (comparación por id-string, 10.7 — venta_ref_id
      // puede estar como número o texto; el resto del código compara con String()).
      const { data: vinc, error } = await client
        .from("facturas_sii_vinculaciones")
        .select("venta_tipo, venta_ref_id, venta_ref_index")
        .eq("venta_tipo", "negocios_carteleria");
      if (!vivo) return;
      if (error) {
        // No tragarse el error (10.177): sin poder verificar los candados, bloquear Guardar.
        setIncompatible(`No se pudieron leer las vinculaciones SII (${error.message}). No es seguro guardar.`);
        setTramos(
          detalle.map((e, i) => ({
            pct: e?.porcentaje != null ? String(e.porcentaje) : "",
            fecha: parseFechaLocal(e?.facturafecha || e?.fecha_factura),
            locked: true,
            preserved: e,
            _idx: i,
          }))
        );
        setCargando(false);
        return;
      }
      const vincIdx = new Set(
        (vinc || [])
          .filter((v) => String(v.venta_ref_id) === String(negocio.id))
          .map((v) => String(v.venta_ref_index ?? ""))
      );
      const estaFacturado = (entry, i) =>
        !!(entry && entry.facturanumero != null && String(entry.facturanumero).trim() !== "") || vincIdx.has(String(i));

      if (detalle.length === 0) {
        // Sin detalle → arranque fresco 75/25 (caso anticipo/contra-entrega típico), editable.
        setTramos([
          { pct: "75", fecha: null, locked: false, preserved: null },
          { pct: "25", fecha: null, locked: false, preserved: null },
        ]);
      } else if (detalle.length === 2 && detalle.every((e) => e && e.porcentaje != null)) {
        // Detalle propio del modelo (2 tramos con %) → precarga por índice. Locked si ya facturado.
        setTramos(
          detalle.map((e, i) => ({
            pct: String(e.porcentaje),
            fecha: parseFechaLocal(e.facturafecha || e.fecha_factura),
            locked: estaFacturado(e, i),
            preserved: e,
          }))
        );
      } else {
        // Historial libre (cantidad ≠ 2, o entries sin "porcentaje") → NO sobrescribir. Aviso + bloqueo.
        setIncompatible(
          `El detalle de facturación de hardware existente (${detalle.length} entrada${
            detalle.length === 1 ? "" : "s"
          }) no encaja en el modelo de 2 tramos por porcentaje. Resolvelo a mano antes de declarar el split; no se sobrescribe automáticamente.`
        );
        setTramos(
          detalle.map((e, i) => ({
            pct: e?.porcentaje != null ? String(e.porcentaje) : "",
            fecha: parseFechaLocal(e?.facturafecha || e?.fecha_factura),
            locked: true,
            preserved: e,
            _idx: i,
          }))
        );
      }
      setCargando(false);
    };
    cargar();
    return () => {
      vivo = false;
    };
  }, [negocio]);

  const setPct = (i, val) => setTramos((prev) => prev.map((t, idx) => (idx === i ? { ...t, pct: val } : t)));
  const setFecha = (i, val) => setTramos((prev) => prev.map((t, idx) => (idx === i ? { ...t, fecha: val } : t)));

  // Materialización %→monto: los tramos locked PRESERVAN su monto guardado; los editables salen del
  // % (round(total*%/100)); el ÚLTIMO tramo EDITABLE absorbe el residuo para que la suma sea EXACTA
  // = monto_hardware. Con 75/25 sobre 13.332.350 → 9.999.263 y 3.333.087.
  const montos = useMemo(() => {
    const lastEditableIdx = tramos.reduce((acc, t, i) => (t.locked ? acc : i), null);
    const base = tramos.map((t) => (t.locked ? redondear(t.preserved?.monto, dec) : null));
    tramos.forEach((t, i) => {
      if (t.locked || i === lastEditableIdx) return;
      base[i] = redondear((montoTotal * (Number(t.pct) || 0)) / 100, dec);
    });
    if (lastEditableIdx != null) {
      const sumOtros = base.reduce((s, m, idx) => (idx === lastEditableIdx ? s : s + (Number(m) || 0)), 0);
      base[lastEditableIdx] = redondear(montoTotal - sumOtros, dec);
    }
    return base;
  }, [tramos, montoTotal, dec]);

  const sumaPct = tramos.reduce((s, t) => s + (Number(t.pct) || 0), 0);
  const pctValido = Math.abs(sumaPct - 100) < 1e-9;
  const montosCuadran = validarSumaCuotas(
    montos.map((m) => ({ monto_cuota: m })),
    montoTotal
  );
  const hayEditable = tramos.some((t) => !t.locked);
  const editablesConFecha = tramos.every((t) => t.locked || !!t.fecha);
  const puedeGuardar =
    !cargando && !guardando && !incompatible && hayEditable && pctValido && montosCuadran && editablesConFecha;

  const guardar = async () => {
    if (!puedeGuardar) return;
    setGuardando(true);
    // Construcción in-place por índice (NUNCA reordenar — venta_ref_index es posicional).
    const nuevos = tramos.map((t, i) => {
      if (t.locked && t.preserved) {
        // Tramo ya facturado: se PRESERVA tal cual (facturanumero/facturafechacobro/plazocobro/monto/%).
        return t.preserved;
      }
      return {
        monto: montos[i],
        facturafecha: fechaISOLocal(t.fecha),
        facturanumero: null,
        facturafechacobro: null,
        plazocobro: null,
        porcentaje: Number(t.pct),
      };
    });
    const { error } = await client
      .from("negocios_carteleria")
      .update({ detalle_pagos_hardware: nuevos })
      .eq("id", negocio.id);
    setGuardando(false);
    if (error) {
      toast.error("Error al guardar el split: " + error.message);
      return;
    }
    toast.success("Split de facturación de hardware guardado");
    if (onSaved) onSaved();
  };

  return (
    <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Facturación hardware — split en 2 tramos</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            <p className="mb-3">
              <strong>Total hardware:</strong> {formatAmount(montoTotal, moneda)}
            </p>

            {incompatible && (
              <div className="alert alert-warning" role="alert">
                {incompatible}
              </div>
            )}

            {cargando ? (
              <div className="text-muted">Cargando…</div>
            ) : (
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>Tramo</th>
                    <th style={{ width: 130 }}>%</th>
                    <th>Fecha factura</th>
                    <th style={{ textAlign: "right" }}>Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {tramos.map((t, i) => (
                    <tr key={i}>
                      <td>
                        Tramo {i + 1}
                        {t.locked && (
                          <span className="badge bg-secondary ms-1" title="Ya facturado — no editable">
                            facturado
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="input-group input-group-sm">
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={t.pct}
                            min={0}
                            max={100}
                            step="any"
                            disabled={t.locked || !!incompatible}
                            onChange={(e) => setPct(i, e.target.value)}
                          />
                          <span className="input-group-text">%</span>
                        </div>
                      </td>
                      <td>
                        <DatePicker
                          selected={t.fecha}
                          onChange={(date) => setFecha(i, date)}
                          dateFormat="dd/MM/yyyy"
                          className="form-control form-control-sm"
                          placeholderText="dd/mm/aaaa"
                          isClearable
                          disabled={t.locked || !!incompatible}
                        />
                      </td>
                      <td style={{ textAlign: "right", fontWeight: "bold" }}>
                        {formatAmount(incompatible ? t.preserved?.monto || 0 : montos[i], moneda)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {!cargando && !incompatible && (
              <div className="mt-2">
                <strong>Suma %:</strong> {sumaPct}
                {!pctValido && <span className="text-danger ms-2">(la suma de los porcentajes debe ser 100)</span>}
                {pctValido && !montosCuadran && (
                  <span className="text-danger ms-2">(los montos no cuadran con el total)</span>
                )}
                {pctValido && montosCuadran && !editablesConFecha && (
                  <span className="text-danger ms-2">(cada tramo editable necesita una fecha de factura)</span>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button className="btn btn-primary" disabled={!puedeGuardar} onClick={guardar}>
              {guardando ? "Guardando…" : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacturacionHardwareModal;
